import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { ApiFailure } from './api-error.ts'

/**
 * The query client, and the one place a failed read or write is inspected.
 *
 * **It is created once, outside React.** docs/ARCHITECTURE.md §7 and
 * docs/ux/PLATFORM-ADMIN.md §5.3: **CF1** is the old app constructing a new `QueryClient`
 * inside its root component, so any theme or breakpoint change replaced the client and
 * wiped every cached read — while a separately configured client sat unimported. This
 * surface has no theme change to trigger it, which is exactly why the rule is written
 * down rather than skipped: here the mistake would sit unnoticed until something else
 * re-rendered the root. `createRuntime()` calls this at module scope; nothing in a
 * component body ever does.
 *
 * `onFailure` is how a 401 that survived the transport's one refresh still ends the
 * session, on **every** screen rather than on the ones that remembered to check (§18.2).
 */
export type QueryClientOptions = {
  readonly onFailure?: (failure: ApiFailure) => void
}

export function createQueryClient(options: QueryClientOptions = {}): QueryClient {
  const report = (error: unknown) => {
    if (error instanceof ApiFailure) {
      options.onFailure?.(error)
    }
  }

  return new QueryClient({
    queryCache: new QueryCache({ onError: report }),
    mutationCache: new MutationCache({ onError: report }),
    defaultOptions: {
      queries: {
        // Retry only what a retry can fix. An `ApiFailure` carrying a real status is the
        // API's answer — retrying a 401, a 404 or a validation failure delays the screen
        // and changes nothing. Status 0 is "the request never got an answer", which is
        // the only case worth another attempt.
        retry: (failureCount, error) =>
          error instanceof ApiFailure && error.status === 0 && failureCount < 2,
        // The estate changes when this one owner changes it, so nothing here is stale
        // enough to be worth a refetch on every window focus.
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
      mutations: {
        // A mutation is the owner having pressed something. It is never retried
        // automatically: the two irreversible actions in this application are the reason
        // (§19), and a duplicate create is the other.
        retry: false,
        // **A write is attempted when the owner presses it, or it fails when pressed — it never
        // waits for the connection.** TanStack Query's own default, `'online'`, pauses a mutation
        // while `onlineManager` reports offline (no request, `isPending` stuck true) and then
        // *sends it by itself* with the original variables when the browser reports online: a
        // password change, a retirement or a key rotation the owner believed had failed, or never
        // saw, landing minutes later (`fix-console-offline-mutations-resume`, QA defect D1 on
        // #374). `'always'` calls `mutationFn` regardless; a request that cannot leave the device
        // rejects, and the transport turns that into `client_offline` at once.
        //
        // The default is here, not on each `useMutation`, so a mutation added later is safe by
        // omission. Queries are left at `'online'` on purpose: a paused read is the same read
        // whenever it runs. See docs/DECISIONS.md, "A console write is attempted when the owner
        // presses it, never queued for the connection".
        networkMode: 'always',
      },
    },
  })
}
